import React, {useEffect, useMemo, useState} from "react";
import {Tooltip, Typography} from "@mui/material";
import {HierarchyTreeTranslationKey} from "../HierarchyTreeTranslationKey";
import {createHierarchyTreeUpdateDto, HierarchyTreeUpdateDto} from "../service/HierarchyTreeUpdateDto";
import hierarchyTreeService from "../service/HierarchyTreeService";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import {SaveButton} from "../../../../../components/button/SaveButton";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {ColorPickerButton} from "../../../../../components/color/ColorPickerButton";
import Snackbar from "../../snackbar/Snackbar";
import {_transl} from "../../../../../store/localization/TranslMessasge";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import {StyleSettingsTranslationKey} from "../../../../../diagram/editor/style/dialog/StyleSettingsTranslationKey";
import {ColorDto, Colors} from "../../../../../common/apis/Color";
import {ICON_REGISTRY, IconName} from "./Icons";
import {IconPickerDialog} from "./IconPickerDialog";
import {UpdateModeToggle} from "../../../../../diagram/editor/style/dialog/UpdateModeToggle";
import {UpdateMode} from "../../../../../diagram/editor/style/StyleSettings";
import Button from "@mui/material/Button";
import {FolderTreeDataDto} from "../service/HierarchyTreeDataDto";
import Constants from "../../../../../common/Constants";
import Grid from "../../../../../components/dialogs/Grid";
import {createStyles, makeStyles} from "@mui/styles";
import {Theme} from "@mui/material/styles";

const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            selectedIcon: {
                width: 46,
                height: 28,
            },
        })
);

export interface FolderStyleUpdateDialogProps {
    open: boolean;
    treeId?: string;
    onConfirm: (treeId: string, treeUpdate: HierarchyTreeUpdateDto<any>) => void;
    onCancel: () => void;
}

export function FolderStyleUpdateDialog({open, treeId, onConfirm, onCancel}: FolderStyleUpdateDialogProps) {
    const classes = useStyles();
    const [treeUpdate, setTreeUpdate] = useState<HierarchyTreeUpdateDto<FolderTreeDataDto> | undefined>(undefined);
    const [openIcon, setOpenIcon] = useState(false);

    const [changeColorMode, setChangeColorMode] = useState<UpdateMode>(UpdateMode.RESET_TO_DEFAULTS);
    const [changeIconMode, setChangeIconMode] = useState<UpdateMode>(UpdateMode.RESET_TO_DEFAULTS);

    const folderDefaultColorDto = Colors.fromCss(Constants.MODEL_FOLDER_ICON_COLOR);
    const FOLDER_TYPE = "folder";

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!open || !treeId) return;

            try {
                const tree = await hierarchyTreeService.findTree(treeId);
                if (!cancelled) {
                    const next = createHierarchyTreeUpdateDto(tree.title, tree.data as FolderTreeDataDto);
                    setTreeUpdate(next);

                    setChangeColorMode(next.data.color ? UpdateMode.USE_CUSTOM : UpdateMode.RESET_TO_DEFAULTS);
                    setChangeIconMode(next.data.icon ? UpdateMode.USE_CUSTOM : UpdateMode.RESET_TO_DEFAULTS);
                }
            } catch (err) {
                if (!cancelled) Snackbar.error(_transl(ErrorTranslationKey.FAILED_TO_LOAD_DATA), err);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [open, treeId]);

    function patchData(patch: Partial<any>) {
        setTreeUpdate((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                data: {
                    ...prev.data,
                    ...patch,
                },
            };
        });
    }

    function changeColor(color: ColorDto) {
        patchData({color});
    }

    function changeIcon(icon: IconName) {
        patchData({icon});
    }

    function handleSave() {
        if (!treeId || !treeUpdate) return;
        onConfirm(treeId, treeUpdate);
        onCancel();
    }

    function handleClose() {
        onCancel();
    }

    function onChangeColorMode(mode: UpdateMode) {
        setChangeColorMode(mode);

        if (mode === UpdateMode.USE_CUSTOM) {
            patchData({color: folderDefaultColorDto});
            return;
        }

        patchData({color: undefined});
    }

    function onChangeIconMode(mode: UpdateMode) {
        setChangeIconMode(mode);

        if (mode === UpdateMode.USE_CUSTOM) {
            patchData({icon: FOLDER_TYPE});
            return;
        }

        patchData({icon: undefined});
    }

    const selectedIconName = treeUpdate?.data?.icon as IconName | undefined;

    const SelectedIcon = useMemo(() => {
        return ICON_REGISTRY[selectedIconName ?? FOLDER_TYPE];
    }, [selectedIconName]);

    return (
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle id="update-folder-style-dialog-title" onDialogClosed={handleClose}
                        title={_transl(HierarchyTreeTranslationKey.STYLE_SETTINGS)}/>
                <DialogContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item container xs={2}>
                            <Typography>{_transl(HierarchyTreeTranslationKey.COLOR)}</Typography>
                        </Grid>
                        <Grid item container xs={6}>
                            <ColorPickerButton
                                    title={_transl(StyleSettingsTranslationKey.CHANGE_COLOR)}
                                    color={treeUpdate?.data?.color || folderDefaultColorDto}
                                    onChange={changeColor}
                                    disabled={changeColorMode !== UpdateMode.USE_CUSTOM}/>
                        </Grid>
                        <Grid item container xs={4} justifyContent={"flex-end"}>
                            <UpdateModeToggle
                                    value={changeColorMode}
                                    hideKeepUnchangedMode={true}
                                    onChange={onChangeColorMode}/>
                        </Grid>

                        <Grid item container xs={2}>
                            <Typography>{_transl(HierarchyTreeTranslationKey.ICON)}</Typography>
                        </Grid>
                        <Grid item container xs={6}>
                            <Tooltip title={_transl(HierarchyTreeTranslationKey.ICON)}>
                                <Button onClick={() => setOpenIcon(true)} variant={"outlined"}
                                        disabled={changeIconMode !== UpdateMode.USE_CUSTOM}>
                                    <SelectedIcon className={classes.selectedIcon}/>
                                </Button>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={4} container justifyContent={"flex-end"}>
                            <UpdateModeToggle value={changeIconMode} hideKeepUnchangedMode={true}
                                    onChange={onChangeIconMode}/>
                        </Grid>
                    </Grid>

                    <IconPickerDialog open={openIcon}
                            selected={selectedIconName}
                            onClose={() => setOpenIcon(false)}
                            onConfirm={(icon) => {
                                changeIcon(icon);
                                setOpenIcon(false);
                            }}/>
                </DialogContent>

                <DialogActions>
                    <CancelButton onClick={handleClose}/>
                    <SaveButton onClick={handleSave} disabled={!treeUpdate || !treeId}/>
                </DialogActions>
            </Dialog>
    );
}